import { useMemo } from "react";

import usePermission from "./usePermission";
import type { AccessQuery } from "../core/access";

/**
 * Synchronously resolves whether the current user satisfies an access query.
 * Recomputes automatically when the user or the query changes.
 *
 * @example
 * const canEdit = useAuthorized({ permissions: "post:edit" });
 */
const useAuthorized = <
    Role extends string = string,
    Permission extends string = string
>(
    query: AccessQuery<Role, Permission>
): boolean => {
    const { can } = usePermission();
    const { roles, permissions, requireAll, check } = query;
    const rolesKey = JSON.stringify(roles ?? null);
    const permissionsKey = JSON.stringify(permissions ?? null);

    return useMemo(
        () => can({ roles, permissions, requireAll, check }),
        // `can` identity changes whenever the current user changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [can, rolesKey, permissionsKey, requireAll, check]
    );
};

export default useAuthorized;
